import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Building2,
  Stethoscope,
  Users,
  CreditCard,
  Calendar,
  ChevronDown,
  RefreshCw,
  Crown,
  BarChart3,
  TrendingUp,
} from "lucide-react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import "../../components/dashboard/chartSetup";
import {
  getSubscriptionDashboardStats,
  SubscriptionDashboardStats,
} from "../../services/subscriptionService";
import { handleApiError } from "../../utils/errorHandler";
import {
  KpiCardSkeleton,
  ChartSkeleton,
  DonutSkeleton,
  TableSkeleton,
} from "../../components/common/Skeleton";
import WidgetErrorState from "../../components/common/WidgetErrorState";
import EmptyState from "../../components/common/EmptyState";
import StaleDataBanner from "../../components/common/StaleDataBanner";

// Helper for Indian compact currency format on Y-axis
const formatInrCompact = (val: number): string => {
  if (val === 0) return "₹ 0";
  if (val >= 10000000) return `₹ ${(val / 10000000).toFixed(val % 10000000 === 0 ? 0 : 1)} Cr`;
  if (val >= 100000) return `₹ ${(val / 100000).toFixed(val % 100000 === 0 ? 0 : 1)}L`;
  if (val >= 1000) return `₹ ${(val / 1000).toFixed(val % 1000 === 0 ? 0 : 1)}k`;
  return `₹ ${val}`;
};

// Date Presets
const PRESET_OPTIONS = [
  { value: "last30", label: "Last 30 Days" },
  { value: "last90", label: "Last 90 Days" },
  { value: "last180", label: "Last 6 Months" },
  { value: "thisYear", label: "This Year" },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom Range" },
];

const PLAN_COLORS = [
  "#8B5CF6", // Purple (Professional)
  "#15BBAE", // Teal (Standard)
  "#0284C7", // Blue (Basic)
  "#F59E0B", // Orange (Annual Starter)
  "#EC4899", // Pink
  "#10B981", // Emerald
  "#6366F1", // Indigo
];

export const SubscriptionDashboard: React.FC = () => {
  const [stats, setStats] = useState<SubscriptionDashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isStale, setIsStale] = useState<boolean>(false);

  // Filter state
  const [selectedPreset, setSelectedPreset] = useState<string>("last180");
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [customStart, setCustomStart] = useState<string>("");
  const [customEnd, setCustomEnd] = useState<string>("");

  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeRequestIdRef = useRef<number>(0);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const fetchStats = useCallback(
    async (presetVal: string, start?: string, end?: string) => {
      const requestId = ++activeRequestIdRef.current;
      setLoading(true);

      try {
        const params: { preset?: string; startDate?: string; endDate?: string } = {};

        if (presetVal === "custom" && start && end) {
          params.startDate = start;
          params.endDate = end;
        } else {
          params.preset = presetVal;
        }

        const data = await getSubscriptionDashboardStats(params);

        // Discard out-of-order or superseded responses
        if (requestId !== activeRequestIdRef.current) return;

        setStats(data);
        setError(null);
        setIsStale(false);
      } catch (err: any) {
        if (requestId !== activeRequestIdRef.current) return;
        const apiError = handleApiError(err);
        setError(apiError.message || "Failed to load dashboard data. Please try again.");
        if (stats) {
          setIsStale(true);
        }
      } finally {
        if (requestId === activeRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [stats]
  );

  useEffect(() => {
    fetchStats(selectedPreset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset]);

  const handlePresetSelect = (presetVal: string) => {
    setSelectedPreset(presetVal);
    if (presetVal !== "custom") {
      setDropdownOpen(false);
      if (selectedPreset === presetVal) {
        fetchStats(presetVal);
      }
    }
  };

  const handleApplyCustom = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customStart || !customEnd) return;
    setSelectedPreset("custom");
    setDropdownOpen(false);
    fetchStats("custom", customStart, customEnd);
  };

  const handleRetry = () => {
    if (selectedPreset === "custom" && customStart && customEnd) {
      fetchStats("custom", customStart, customEnd);
    } else {
      fetchStats(selectedPreset);
    }
  };

  // 1. Subscription Revenue Trend Chart Config
  const revenueTrendData = useMemo(() => {
    if (!stats || !stats.revenueTrend) return { labels: [], datasets: [] };

    const labels = stats.revenueTrend.map((t) => t.month);
    return {
      labels,
      datasets: [
        {
          label: "Hospital Revenue",
          data: stats.revenueTrend.map((t) => t.hospitalRevenue),
          borderColor: "#15BBAE",
          backgroundColor: "rgba(21, 187, 174, 0.12)",
          fill: true,
          tension: 0.35,
          borderWidth: 2.5,
          pointRadius: 4,
          pointBackgroundColor: "#15BBAE",
          pointHoverRadius: 6,
        },
        {
          label: "Doctor Revenue",
          data: stats.revenueTrend.map((t) => t.doctorRevenue),
          borderColor: "#E84570",
          backgroundColor: "rgba(232, 69, 112, 0.10)",
          fill: true,
          tension: 0.35,
          borderWidth: 2.5,
          pointRadius: 4,
          pointBackgroundColor: "#E84570",
          pointHoverRadius: 6,
        },
        {
          label: "Public User Revenue",
          data: stats.revenueTrend.map((t) => t.publicRevenue),
          borderColor: "#8B5CF6",
          backgroundColor: "rgba(139, 92, 246, 0.08)",
          fill: true,
          tension: 0.35,
          borderWidth: 2.5,
          pointRadius: 4,
          pointBackgroundColor: "#8B5CF6",
          pointHoverRadius: 6,
        },
      ],
    };
  }, [stats]);

  const revenueTrendOptions: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#16181D",
          padding: 12,
          titleColor: "#FFFFFF",
          bodyColor: "rgba(255,255,255,0.9)",
          cornerRadius: 8,
          callbacks: {
            label: (ctx: any) => {
              const label = ctx.dataset.label || "";
              const val = ctx.parsed.y || 0;
              return `${label}: ₹ ${new Intl.NumberFormat("en-IN").format(val)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: "#63788E", font: { size: 12 } },
        },
        y: {
          beginAtZero: true,
          grid: { color: "#F0F4F8" },
          ticks: {
            color: "#63788E",
            font: { size: 12 },
            callback: (val: any) => formatInrCompact(Number(val)),
          },
        },
      },
    }),
    []
  );

  // 2. Plan Distribution Chart Config
  const planDistributionData = useMemo(() => {
    if (!stats || !stats.planDistribution) return { labels: [], datasets: [] };

    const labels = stats.planDistribution.map((p) => p.planName);
    return {
      labels,
      datasets: [
        {
          label: "Hospitals",
          data: stats.planDistribution.map((p) => p.hospitals),
          backgroundColor: "#15BBAE",
          borderRadius: 4,
          barPercentage: 0.7,
          categoryPercentage: 0.6,
        },
        {
          label: "Doctors",
          data: stats.planDistribution.map((p) => p.doctors),
          backgroundColor: "#E84570",
          borderRadius: 4,
          barPercentage: 0.7,
          categoryPercentage: 0.6,
        },
        {
          label: "Public Users",
          data: stats.planDistribution.map((p) => p.publicUsers),
          backgroundColor: "#8B5CF6",
          borderRadius: 4,
          barPercentage: 0.7,
          categoryPercentage: 0.6,
        },
      ],
    };
  }, [stats]);

  const planDistributionOptions: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#16181D",
          padding: 10,
          titleColor: "#FFFFFF",
          bodyColor: "rgba(255,255,255,0.9)",
          cornerRadius: 6,
          callbacks: {
            label: (ctx: any) => {
              const label = ctx.dataset.label || "";
              const val = ctx.parsed.y || 0;
              return `${label}: ${val} subscriptions`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: "#63788E", font: { size: 12 } },
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, color: "#63788E", font: { size: 12 } },
          grid: { color: "#F0F4F8" },
        },
      },
    }),
    []
  );

  // 3. Subscription Usage Donut Chart Config
  const usageChartData = useMemo(() => {
    if (!stats || !stats.subscriptionUsage?.statuses) return { labels: [], datasets: [] };

    const statuses = stats.subscriptionUsage.statuses;
    return {
      labels: statuses.map((s) => s.label),
      datasets: [
        {
          data: statuses.map((s) => s.count),
          backgroundColor: statuses.map((s) => s.color),
          borderColor: "#FFFFFF",
          borderWidth: 2,
        },
      ],
    };
  }, [stats]);

  const usageChartOptions: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1,
      cutout: "72%",
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#16181D",
          padding: 10,
          cornerRadius: 6,
          callbacks: {
            label: (ctx: any) => {
              const item = stats?.subscriptionUsage?.statuses[ctx.dataIndex];
              if (!item) return `${ctx.label}: ${ctx.parsed}`;
              const byType = item.byType
                ? ` (H: ${item.byType.hospital}, D: ${item.byType.doctor}, P: ${item.byType.public})`
                : "";
              return `${item.label}: ${item.count} (${item.percentage}%)${byType}`;
            },
          },
        },
      },
    }),
    [stats]
  );

  // Checks for empty states across sections
  const isUsageEmpty = useMemo(() => {
    if (!stats || !stats.subscriptionUsage) return true;
    return (
      stats.subscriptionUsage.total === 0 ||
      stats.subscriptionUsage.statuses.every((s) => s.count === 0)
    );
  }, [stats]);

  const isPlanDistributionEmpty = useMemo(() => {
    if (!stats || !stats.planDistribution || stats.planDistribution.length === 0) return true;
    return stats.planDistribution.every(
      (p) => (p.hospitals || 0) + (p.doctors || 0) + (p.publicUsers || 0) === 0
    );
  }, [stats]);

  const isRevenueTrendEmpty = useMemo(() => {
    if (!stats || !stats.revenueTrend || stats.revenueTrend.length === 0) return true;
    return stats.revenueTrend.every(
      (t) => (t.hospitalRevenue || 0) + (t.doctorRevenue || 0) + (t.publicRevenue || 0) === 0
    );
  }, [stats]);

  const isTopPlansEmpty = useMemo(() => {
    if (!stats || !stats.topPlans || stats.topPlans.length === 0) return true;
    return stats.topPlans.every((p) => p.subscriptions === 0 && p.revenue === 0);
  }, [stats]);

  return (
    <div className="space-y-6 pb-12">
      {/* ── Top Header Section ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3.5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E6F7F5] text-[#15BBAE] shadow-sm">
            <Crown size={22} className="stroke-[2.2]" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-[#10253A] sm:text-2xl">
              Subscription Dashboard
            </h1>
            <p className="text-xs font-medium text-[#63788E] sm:text-sm">
              Overview of hospital, doctor, and public user subscriptions, usage and billing details
            </p>
          </div>
        </div>

        {/* Date Range Selector Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            id="date-range-btn"
            data-testid="date-range-btn"
            disabled={loading && !stats}
            onClick={() => setDropdownOpen((prev) => !prev)}
            className="inline-flex items-center gap-2.5 rounded-lg border border-[#E4EAF0] bg-white px-4 py-2.5 text-xs font-medium text-[#10253A] shadow-sm transition-all hover:bg-neutral-50 disabled:opacity-60 sm:text-sm cursor-pointer"
          >
            {loading ? (
              <RefreshCw size={15} className="animate-spin text-[#15BBAE]" />
            ) : (
              <Calendar size={16} className="text-[#15BBAE]" />
            )}
            <span id="date-range-label">{stats?.dateRange?.label || "Select Date Range"}</span>
            <ChevronDown
              size={15}
              className={`text-[#63788E] transition-transform duration-200 ${
                dropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 z-30 mt-2 w-72 rounded-xl border border-[#E4EAF0] bg-white p-2 shadow-xl animate-in fade-in zoom-in-95">
              <div className="space-y-1">
                {PRESET_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handlePresetSelect(opt.value)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium rounded-lg text-left transition-colors ${
                      selectedPreset === opt.value
                        ? "bg-[#E6F7F5] text-[#15BBAE] font-semibold"
                        : "text-[#10253A] hover:bg-[#F7F9FB]"
                    }`}
                  >
                    <span>{opt.label}</span>
                    {selectedPreset === opt.value && (
                      <span className="h-1.5 w-1.5 rounded-full bg-[#15BBAE]" />
                    )}
                  </button>
                ))}
              </div>

              {selectedPreset === "custom" && (
                <form
                  onSubmit={handleApplyCustom}
                  className="mt-2.5 border-t border-[#F0F4F8] pt-2.5 px-1 space-y-2"
                >
                  <div>
                    <label className="block text-[11px] font-medium text-[#63788E] mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      required
                      className="w-full rounded-lg border border-[#E4EAF0] px-2.5 py-1.5 text-xs text-[#10253A] focus:border-[#15BBAE] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-[#63788E] mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      required
                      className="w-full rounded-lg border border-[#E4EAF0] px-2.5 py-1.5 text-xs text-[#10253A] focus:border-[#15BBAE] focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 rounded-lg bg-[#15BBAE] py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-[#13a89d] transition-colors disabled:opacity-60 cursor-pointer"
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      onClick={() => setDropdownOpen(false)}
                      className="rounded-lg border border-[#E4EAF0] px-3 py-1.5 text-xs font-medium text-[#63788E] hover:bg-neutral-50 transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Stale Data Warning Banner (when background refresh fails) ── */}
      {isStale && (
        <StaleDataBanner
          message="Failed to retrieve latest subscription statistics. Displaying previously cached data."
          onRetry={handleRetry}
          isRetrying={loading}
        />
      )}

      {/* ── Hard Error State (Initial load failure) ── */}
      {error && !stats && (
        <WidgetErrorState
          title="Subscription Dashboard Unavailable"
          message={error}
          onRetry={handleRetry}
          isRetrying={loading}
          variant="card"
          className="my-4"
        />
      )}

      {/* ── Initial Loading Skeleton ── */}
      {loading && !stats && (
        <div className="space-y-6">
          <KpiCardSkeleton count={5} />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <DonutSkeleton className="lg:col-span-5" />
            <ChartSkeleton className="lg:col-span-7" height={260} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <TableSkeleton className="lg:col-span-5" rows={4} columns={3} />
            <ChartSkeleton className="lg:col-span-7" height={260} />
          </div>
        </div>
      )}

      {/* ── Loaded Dashboard Content ── */}
      {stats && (
        <>
          {/* ── 1. Five KPI Widgets ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {/* Card 1: Total Hospitals */}
            <div className="flex flex-col justify-between rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm transition-all hover:shadow-md">
              <div>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-[#63788E]">Total Hospitals</p>
                    <h3 className="mt-1 text-2xl font-bold tracking-tight text-[#10253A]">
                      {stats.kpis.totalHospitals.count}
                    </h3>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E6F7F5] text-[#15BBAE]">
                    <Building2 size={20} />
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t border-[#F0F4F8] pt-3 flex items-center gap-2.5 text-[11px] text-[#63788E]">
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#15BBAE]" />
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalHospitals.breakdown.active}
                  </strong>{" "}
                  Active
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalHospitals.breakdown.pending}
                  </strong>{" "}
                  Pending
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#E84570]" />
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalHospitals.breakdown.expired}
                  </strong>{" "}
                  Expired
                </span>
              </div>
            </div>

            {/* Card 2: Total Doctors */}
            <div className="flex flex-col justify-between rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm transition-all hover:shadow-md">
              <div>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-[#63788E]">Total Doctors</p>
                    <h3 className="mt-1 text-2xl font-bold tracking-tight text-[#10253A]">
                      {stats.kpis.totalDoctors.count}
                    </h3>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EDE9FE] text-[#7C3AED]">
                    <Stethoscope size={20} />
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t border-[#F0F4F8] pt-3 flex items-center gap-2.5 text-[11px] text-[#63788E]">
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#15BBAE]" />
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalDoctors.breakdown.active}
                  </strong>{" "}
                  Active
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalDoctors.breakdown.pending}
                  </strong>{" "}
                  Pending
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#E84570]" />
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalDoctors.breakdown.expired}
                  </strong>{" "}
                  Expired
                </span>
              </div>
            </div>

            {/* Card 3: Total Public Users */}
            <div className="flex flex-col justify-between rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm transition-all hover:shadow-md">
              <div>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-[#63788E]">Total Public Users</p>
                    <h3 className="mt-1 text-2xl font-bold tracking-tight text-[#10253A]">
                      {stats.kpis.totalPublicUsers.count}
                    </h3>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E0F2FE] text-[#0284C7]">
                    <Users size={20} />
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t border-[#F0F4F8] pt-3 flex items-center gap-2.5 text-[11px] text-[#63788E]">
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#15BBAE]" />
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalPublicUsers.breakdown.active}
                  </strong>{" "}
                  Active
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalPublicUsers.breakdown.pending}
                  </strong>{" "}
                  Pending
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#E84570]" />
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalPublicUsers.breakdown.expired}
                  </strong>{" "}
                  Expired
                </span>
              </div>
            </div>

            {/* Card 4: Total Subscriptions */}
            <div className="flex flex-col justify-between rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm transition-all hover:shadow-md">
              <div>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-[#63788E]">Total Subscriptions</p>
                    <h3 className="mt-1 text-2xl font-bold tracking-tight text-[#10253A]">
                      {stats.kpis.totalSubscriptions.count}
                    </h3>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FDF2F8] text-[#DB2777]">
                    <CreditCard size={20} />
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t border-[#F0F4F8] pt-3 flex items-center gap-2.5 text-[11px] text-[#63788E]">
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#15BBAE]" />
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalSubscriptions.breakdown.active}
                  </strong>{" "}
                  Active
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalSubscriptions.breakdown.pending}
                  </strong>{" "}
                  Pending
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#E84570]" />
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalSubscriptions.breakdown.expired}
                  </strong>{" "}
                  Expired
                </span>
              </div>
            </div>

            {/* Card 5: Total Revenue (INR) */}
            <div className="flex flex-col justify-between rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm transition-all hover:shadow-md">
              <div>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-[#63788E]">Total Revenue (This Period)</p>
                    <h3 className="mt-1 text-xl lg:text-2xl font-bold tracking-tight text-[#10253A]">
                      {stats.kpis.totalRevenue.formatted}
                    </h3>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#ECFDF5] text-[#059669] font-bold text-lg">
                    ₹
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t border-[#F0F4F8] pt-3 text-[11px] text-[#63788E] flex flex-wrap items-center justify-between gap-1">
                <span>
                  Hospital:{" "}
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalRevenue.breakdown.hospitalRevenueFormatted}
                  </strong>
                </span>
                <span>
                  Doctor:{" "}
                  <strong className="text-[#10253A] font-semibold">
                    {stats.kpis.totalRevenue.breakdown.doctorRevenueFormatted}
                  </strong>
                </span>
                {stats.kpis.totalRevenue.breakdown.publicRevenue > 0 && (
                  <span>
                    Public:{" "}
                    <strong className="text-[#10253A] font-semibold">
                      {stats.kpis.totalRevenue.breakdown.publicRevenueFormatted}
                    </strong>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── 2. Middle Section: Subscription Usage & Plan Distribution ── */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Chart 1: Subscription Usage (5 cols ≈ 41.67%) */}
            <div className="lg:col-span-5 rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm relative">
              <div className="pb-4 border-b border-[#F0F4F8]">
                <h3 className="text-sm font-semibold text-[#10253A]">Subscription Usage</h3>
                <p className="text-[11px] text-[#63788E]">Status breakdown across all subscribers</p>
              </div>

              {isUsageEmpty ? (
                <EmptyState
                  icon={<CreditCard size={22} />}
                  title="No subscription usage recorded"
                  description="No subscriber status entries found for the selected period."
                  action={{
                    label: "View Plans",
                    to: "/super-admin/subscription",
                  }}
                  variant="chart"
                />
              ) : (
                <div className="mt-4 flex flex-col items-center justify-center">
                  <div className="relative h-44 w-44 flex items-center justify-center">
                    <Doughnut
                      key={`donut-${stats?.dateRange?.startDate}-${stats?.dateRange?.endDate}-${stats?.subscriptionUsage?.total}`}
                      data={usageChartData}
                      options={usageChartOptions}
                    />
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
                      <span className="text-2xl font-bold tracking-tight text-[#10253A]">
                        {stats?.subscriptionUsage?.total ?? 0}
                      </span>
                      <span className="text-[11px] font-medium text-[#63788E]">
                        Total Subscriptions
                      </span>
                    </div>
                  </div>

                  {/* Status Legend on the bottom/side */}
                  <div className="mt-4 w-full border-t border-[#F0F4F8] pt-3 flex justify-center">
                    <ul className="w-full max-w-[260px] space-y-2">
                      {stats.subscriptionUsage.statuses.map((st) => (
                        <li
                          key={st.label}
                          className="flex items-center justify-between text-xs text-[#10253A]"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="h-2.5 w-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: st.color }}
                            />
                            <span className="font-medium text-[#63788E]">{st.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-[#10253A]">{st.count}</span>
                            <span className="w-10 text-right text-[11px] font-medium text-[#63788E]">
                              ({st.percentage}%)
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* In-flight loading overlay during refresh */}
              {loading && (
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center rounded-xl z-10">
                  <div className="flex items-center gap-2 rounded-full border border-[#E4EAF0] bg-white px-3 py-1 text-xs font-semibold text-[#63788E] shadow-sm">
                    <RefreshCw size={12} className="animate-spin text-[#15BBAE]" />
                    Updating...
                  </div>
                </div>
              )}
            </div>

            {/* Chart 2: Plan Distribution (7 cols ≈ 58.33%) */}
            <div className="lg:col-span-7 rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm relative">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-[#F0F4F8]">
                <div>
                  <h3 className="text-sm font-semibold text-[#10253A]">Plan Distribution</h3>
                  <p className="text-[11px] text-[#63788E]">Hospitals, Doctors & Public</p>
                </div>
                <div className="flex items-center gap-2.5 text-xs text-[#63788E]">
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-sm bg-[#15BBAE]" />
                    Hospitals
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-sm bg-[#E84570]" />
                    Doctors
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-sm bg-[#8B5CF6]" />
                    Public
                  </span>
                </div>
              </div>

              {isPlanDistributionEmpty ? (
                <EmptyState
                  icon={<BarChart3 size={24} />}
                  title="No plan distribution data"
                  description="No subscribers are associated with plans in this period."
                  action={{
                    label: "Manage Plans",
                    to: "/super-admin/subscription",
                  }}
                  variant="chart"
                />
              ) : (
                <div className="relative mt-4 h-64 w-full">
                  <Bar data={planDistributionData} options={planDistributionOptions} />
                </div>
              )}

              {loading && (
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center rounded-xl z-10">
                  <div className="flex items-center gap-2 rounded-full border border-[#E4EAF0] bg-white px-3 py-1 text-xs font-semibold text-[#63788E] shadow-sm">
                    <RefreshCw size={12} className="animate-spin text-[#15BBAE]" />
                    Updating...
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── 3. Bottom Section: Top Revenue Plans & Revenue Trend ── */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Left Column: Top Revenue Generating Plans Table (5 cols ≈ 41.67%) */}
            <div className="lg:col-span-5 rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm relative">
              <div className="pb-3 border-b border-[#F0F4F8]">
                <h3 className="text-sm font-bold text-[#10253A]">
                  Top Revenue Generating Plans
                </h3>
              </div>

              {isTopPlansEmpty ? (
                <EmptyState
                  icon={<CreditCard size={22} />}
                  title="No revenue generated"
                  description="No subscription plans recorded revenue during this period."
                  action={{
                    label: "Create Plan",
                    to: "/super-admin/subscription/new",
                  }}
                  variant="table"
                />
              ) : (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-[#F0F4F8] text-xs font-semibold text-[#63788E]">
                        <th className="pb-3 pl-1 font-semibold">Plan Name</th>
                        <th className="pb-3 text-center font-semibold">Subscriptions</th>
                        <th className="pb-3 pr-1 text-right font-semibold">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F0F4F8]">
                      {stats.topPlans!.map((plan, idx) => (
                        <tr
                          key={plan.planId || idx}
                          className="hover:bg-[#F8FAFC] transition-colors"
                        >
                          <td className="py-3.5 pl-1 font-medium text-[#10253A]">
                            <div className="flex items-center gap-2.5">
                              <span
                                className="h-2.5 w-2.5 rounded-full shrink-0"
                                style={{
                                  backgroundColor: PLAN_COLORS[idx % PLAN_COLORS.length],
                                }}
                              />
                              <span className="text-[13px] font-medium text-[#10253A]">
                                {plan.planName}
                              </span>
                            </div>
                          </td>
                          <td className="py-3.5 text-center text-[13px] font-medium text-[#10253A]">
                            {plan.subscriptions}
                          </td>
                          <td className="py-3.5 pr-1 text-right text-[13px] font-bold text-[#10253A]">
                            {plan.revenueFormatted}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {loading && (
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center rounded-xl z-10">
                  <div className="flex items-center gap-2 rounded-full border border-[#E4EAF0] bg-white px-3 py-1 text-xs font-semibold text-[#63788E] shadow-sm">
                    <RefreshCw size={12} className="animate-spin text-[#15BBAE]" />
                    Updating...
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: Subscription Revenue Trend (7 cols ≈ 58.33%) */}
            <div className="lg:col-span-7 rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm relative">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-[#F0F4F8]">
                <h3 className="text-sm font-bold text-[#10253A]">
                  Subscription Revenue Trend
                </h3>
                <div className="flex flex-wrap items-center gap-3 text-xs text-[#63788E]">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#15BBAE]" />
                    Hospital Revenue
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#E84570]" />
                    Doctor Revenue
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#8B5CF6]" />
                    Public User
                  </span>
                </div>
              </div>

              {isRevenueTrendEmpty ? (
                <EmptyState
                  icon={<TrendingUp size={24} />}
                  title="No revenue trend recorded"
                  description="No billing activity was recorded for this period. Try expanding the date range."
                  action={{
                    label: "Show All Time",
                    onClick: () => handlePresetSelect("all"),
                  }}
                  variant="chart"
                />
              ) : (
                <div className="relative mt-4 h-64 w-full">
                  <Line data={revenueTrendData} options={revenueTrendOptions} />
                </div>
              )}

              {loading && (
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center rounded-xl z-10">
                  <div className="flex items-center gap-2 rounded-full border border-[#E4EAF0] bg-white px-3 py-1 text-xs font-semibold text-[#63788E] shadow-sm">
                    <RefreshCw size={12} className="animate-spin text-[#15BBAE]" />
                    Updating...
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionDashboard;
